import React from 'react';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from '../i18n';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../utils/languageUtils';

/**
 * 日期时间范围选择器
 * @description 同delivery/components/DateTimeRangeFilterItem，原组件位于delivery模块，现将组件抽离至components公用模块，便于复用
 *              原引用delivery模块的组件未迁移至公用模块，仍需引用delivery模块的组件
 *              新开发可使用该公用组件
 */
interface DateTimeRangePickerProps {
    label: string;
    value?: [string | null, string | null] | null;
    onChange?: (dates: [string | null, string | null] | null) => void;
    className?: string;
    spanClassName?: string;
    placeholder?: [string, string];
    format?: string;
    showTime?: boolean | object;
    allowClear?: boolean;
    size?: 'small' | 'middle' | 'large';
    disabled?: boolean;
    hideLabel?: boolean;
}

const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
    label,
    value,
    onChange,
    className = '',
    spanClassName = '',
    placeholder,
    format = 'YYYY/MM/DD HH:mm:ss',
    showTime = {
        format: 'HH:mm:ss',
        defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
    },
    allowClear = true,
    size = 'small',
    disabled = false,
    hideLabel = false
}) => {
    // 将字符串转换为 Dayjs 对象以供 DatePicker 使用，value 非法格式时按未选处理
    const dayjsValue = value && Array.isArray(value) && value.length === 2 ? [
        value[0] ? dayjs(value[0]) : null,
        value[1] ? dayjs(value[1]) : null
    ] as [Dayjs | null, Dayjs | null] : null;

    // 处理 DatePicker 的变更，输出不带时区的本地时间串（用户所见即所选），时区换算交给调用方
    const handleChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        if (!onChange) return;
        if (dates && dates[0] && dates[1]) {
            onChange([dates[0].format('YYYY-MM-DD HH:mm:ss'), dates[1].format('YYYY-MM-DD HH:mm:ss')]);
        } else {
            onChange(null);
        }
    };

    const defaultPlaceholder: [string, string] = [
        t('components.input.start') || 'Start Time',
        t('components.input.end') || 'End Time'
    ];

    const containerClassName = useFilterContainerClassName(className);
    const labelClassName = useFilterLabelClassName(spanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    return (
        <div className={containerClassName}>
            {!hideLabel && (
                <span className={labelClassName}>
                    {label}
                </span>
            )}
            <div className={inputContainerClassName}>
                <DatePicker.RangePicker
                    value={dayjsValue}
                    onChange={handleChange}
                    className="w-full h-8"
                    placeholder={placeholder || defaultPlaceholder}
                    format={format}
                    showTime={showTime}
                    size={size}
                    allowClear={allowClear}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

export default DateTimeRangePicker;
